<template>
  <div class="tw">
    <ContentSkeleton v-if="loading && !loaded" variant="list" :rows="4" />
    <p v-else-if="error" class="dw-empty">{{ $t("dashboardWidgets.loadError") }}</p>
    <p v-else-if="!active.length" class="dw-empty">{{ t("noActive") }}</p>

    <!-- Estado: cuántos hay en cada situación. -->
    <template v-else-if="view === 'status'">
      <div class="tw-main">
        <span class="tw-main__value">{{ active.length }}</span>
        <span class="tw-main__label">{{ t("activeTrackings") }}</span>
      </div>
      <div class="tw-tiles">
        <router-link
          v-for="s in stateCounts"
          :key="s.key"
          :to="pageLink"
          class="tw-tile"
          :class="`tw-tile--${s.key}`"
        >
          <span class="tw-tile__value">{{ s.count }}</span>
          <span class="tw-tile__label">{{ t(`state.${s.key}`) }}</span>
        </router-link>
      </div>
      <p v-if="stoppedCount" class="tw-note">{{ t("stoppedCount", { n: stoppedCount }) }}</p>
    </template>

    <!-- Alertas: lo que ya debería haber pasado, errores de la naviera. -->
    <template v-else-if="view === 'alerts'">
      <p v-if="!alerts.length" class="tw-ok">
        <i class="fas fa-check-circle" aria-hidden="true"></i> {{ t("noAlerts") }}
      </p>
      <ul v-else class="tw-list">
        <li v-for="a in alerts" :key="a.key" class="tw-row">
          <span class="tw-row__icon" :class="`is-${a.kind}`" aria-hidden="true">
            <i class="fas" :class="a.kind === 'error' ? 'fa-plug' : 'fa-exclamation-triangle'"></i>
          </span>
          <span class="tw-row__body">
            <router-link :to="a.to" class="tw-row__title">{{ a.title }}</router-link>
            <span class="tw-row__text">{{ a.text }}</span>
          </span>
        </li>
      </ul>
    </template>

    <!-- Próximos arribos: por ETA, los atrasados primero. -->
    <template v-else-if="view === 'arrivals'">
      <p v-if="!arrivals.length" class="dw-empty">{{ t("noArrivals") }}</p>
      <ul v-else class="tw-list">
        <li v-for="a in arrivals" :key="a.key" class="tw-row">
          <span class="tw-date" :class="{ 'is-late': a.days < 0, 'is-soon': a.days >= 0 && a.days <= 3 }">
            <span class="tw-date__day">{{ a.day }}</span>
            <span class="tw-date__month">{{ a.month }}</span>
          </span>
          <span class="tw-row__body">
            <span class="tw-row__line">
              <router-link :to="a.to" class="tw-row__title">{{ a.title }}</router-link>
              <span class="tw-row__when" :class="{ 'is-late': a.days < 0 }">{{ a.when }}</span>
            </span>
            <span class="tw-row__text">
              {{ a.route }}
              <template v-if="a.shipping">
                ·
                <router-link v-if="a.shippingTo" :to="a.shippingTo">{{ a.shipping }}</router-link>
                <span v-else>{{ a.shipping }}</span>
              </template>
            </span>
          </span>
        </li>
      </ul>
    </template>

    <!-- Rutas: origen → destino de lo activo. -->
    <template v-else>
      <div v-for="r in routes" :key="r.label" class="tw-route" :title="`${r.label}: ${r.count}`">
        <span class="tw-route__label">
          <span>{{ r.from || "—" }}</span>
          <i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i>
          <span>{{ r.to || "—" }}</span>
        </span>
        <span class="tw-route__track"><span class="tw-route__fill" :style="{ width: `${r.pct}%` }"></span></span>
        <span class="tw-route__value">{{ r.count }}</span>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Seguimiento de contenedores (módulo FindTeu): estado, alertas, próximos arribos y
 * rutas. Solo cuenta los trackings activos; los detenidos son historia.
 */
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { buildDynamicItemPrettySlug } from "@/utils/dynamicModelPaths";
import { getTrackings, invalidateTrackings, trackingState, trackingLink, daysFromToday, TRACKING_STATES } from "../trackingData";

const MAX_ROWS = 25;
const MAX_ROUTES = 8;

export default {
  name: "TrackingWidget",
  components: { ContentSkeleton },
  props: {
    entry: { type: Object, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return { loading: false, loaded: false, error: false, trackings: [] };
  },
  computed: {
    view() {
      return (this.entry.config && this.entry.config.view) || "status";
    },
    pageLink() {
      return "/ft/container-tracking";
    },
    locale() {
      return (this.$i18n && this.$i18n.locale) || "es";
    },
    active() {
      return this.trackings.filter((t) => t.isActive !== false);
    },
    stoppedCount() {
      return this.trackings.length - this.active.length;
    },
    stateCounts() {
      const counts = {};
      this.active.forEach((t) => {
        const s = trackingState(t);
        counts[s] = (counts[s] || 0) + 1;
      });
      return TRACKING_STATES.filter((k) => counts[k] || ["inTransit", "delayed"].includes(k)).map((key) => ({
        key,
        count: counts[key] || 0,
      }));
    },
    alerts() {
      const out = [];
      this.active.forEach((t) => {
        if (t.completed) return;
        const m = t.milestones || {};
        const base = { title: this.titleOf(t), to: trackingLink(t) };
        if (t.lastSyncError && t.lastSyncError.code != null) {
          out.push({ ...base, key: `${t.trackingNumber}-err`, kind: "error", weight: 1000, text: this.t("syncError", { code: t.lastSyncError.code, text: t.lastSyncError.text || "—" }) });
        }
        if (m.overdue) {
          out.push({
            ...base,
            key: `${t.trackingNumber}-ms`,
            kind: "overdue",
            weight: m.overdue.daysOverdue,
            text: this.$t("findTeu.milestoneOverdue", { days: m.overdue.daysOverdue, action: m.overdue.action || "—", port: m.overdue.port || "—" }),
          });
        } else if (m.etaOverdue) {
          out.push({
            ...base,
            key: `${t.trackingNumber}-eta`,
            kind: "overdue",
            weight: m.etaOverdue.daysOverdue,
            text: this.$t("findTeu.milestoneEtaOverdue", { days: m.etaOverdue.daysOverdue }),
          });
        }
      });
      return out.sort((a, b) => b.weight - a.weight).slice(0, MAX_ROWS);
    },
    arrivals() {
      const fmtDay = new Intl.DateTimeFormat(this.locale, { day: "2-digit", timeZone: "UTC" });
      const fmtMonth = new Intl.DateTimeFormat(this.locale, { month: "short", timeZone: "UTC" });
      return this.active
        .filter((t) => !t.completed && t.eta)
        .map((t) => {
          const days = daysFromToday(t.eta);
          const d = new Date(`${String(t.eta).slice(0, 10)}T00:00:00Z`);
          const shipping = (t.linkedShippings || [])[0];
          return {
            key: `${t.trackingType}-${t.trackingNumber}`,
            days,
            day: fmtDay.format(d),
            month: fmtMonth.format(d).replace(".", ""),
            when: this.whenText(days),
            title: t.trackingNumber,
            to: trackingLink(t),
            route: [t.origin, t.destination].filter(Boolean).join(" → "),
            shipping: shipping ? shipping.label : "",
            shippingTo: shipping && shipping.itemId ? `/i/${buildDynamicItemPrettySlug(shipping.modelName || "shipping", shipping.itemId, shipping.label)}` : null,
          };
        })
        .sort((a, b) => a.days - b.days)
        .slice(0, MAX_ROWS);
    },
    routes() {
      const map = new Map();
      this.active
        .filter((t) => !t.completed)
        .forEach((t) => {
          const key = `${t.origin || ""}→${t.destination || ""}`;
          const r = map.get(key) || { label: key, from: t.origin, to: t.destination, count: 0 };
          r.count += 1;
          map.set(key, r);
        });
      const rows = Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, MAX_ROUTES);
      const max = Math.max(1, ...rows.map((r) => r.count));
      return rows.map((r) => ({ ...r, pct: Math.max(4, Math.round((r.count / max) * 100)) }));
    },
    menuItems() {
      return [{ key: "open", label: this.$tn("dashboardWidgets.tracking.open"), icon: "external-link-alt" }];
    },
  },
  watch: {
    menuItems: {
      immediate: true,
      handler(items) {
        this.$emit("menu", items);
      },
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    t(key, params) {
      return this.$t(`dashboardWidgets.tracking.${key}`, params);
    },
    titleOf(t) {
      const shipping = (t.linkedShippings || [])[0];
      return shipping && shipping.label ? `${t.trackingNumber} · ${shipping.label}` : t.trackingNumber;
    },
    whenText(days) {
      if (days === null) return "";
      if (days === 0) return this.t("today");
      if (days < 0) return this.t("daysLate", { n: -days });
      return this.t("inDays", { n: days });
    },
    async load(force = false) {
      this.loading = true;
      this.error = false;
      try {
        this.trackings = await getTrackings({ force });
        this.loaded = true;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    /** «Actualizar» del marco: sin esto el remontaje usaría la caché de 60 s. */
    beforeRefresh() {
      invalidateTrackings();
    },
    onMenu(key) {
      if (key === "open") this.$router.push(this.pageLink);
    },
  },
};
</script>

<style scoped>
.tw { display: flex; flex-direction: column; gap: 10px; }
.tw-main { display: flex; align-items: baseline; gap: 8px; }
.tw-main__value { font-size: 2rem; font-weight: 700; line-height: 1; color: var(--bs-primary); }
.tw-main__label { font-size: 0.85rem; color: var(--dw-muted); }
.tw-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(92px, 1fr)); gap: 6px; }
.tw-tile {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 8px;
  background: var(--dw-surface-2);
  border-left: 3px solid var(--dw-border);
  color: var(--dw-text);
  text-decoration: none;
}
.tw-tile:hover { color: var(--dw-text); filter: brightness(0.97); }
.tw-tile--inTransit { border-left-color: var(--bs-primary); }
.tw-tile--notSailed { border-left-color: var(--dw-line-strong); }
.tw-tile--delayed { border-left-color: var(--bs-warning); }
.tw-tile--error { border-left-color: var(--bs-danger); }
.tw-tile--completed { border-left-color: var(--bs-success); }
.tw-tile__value { font-weight: 700; font-size: 1.1rem; }
.tw-tile__label { font-size: 0.72rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tw-note { margin: 0; font-size: 0.72rem; color: var(--dw-muted); }
.tw-ok { margin: 0; padding: 14px 0; text-align: center; font-size: 0.85rem; color: var(--dw-muted); }
.tw-ok i { color: var(--bs-success); margin-right: 4px; }
.tw-list { list-style: none; margin: 0; padding: 0; }
.tw-row { display: flex; gap: 10px; align-items: flex-start; padding: 7px 0; border-bottom: 1px solid var(--dw-border); }
.tw-row:last-child { border-bottom: 0; }
.tw-row__icon { width: 26px; height: 26px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 0.75rem; }
.tw-row__icon.is-overdue { background: rgba(var(--bs-warning-rgb), 0.16); color: var(--bs-warning); }
.tw-row__icon.is-error { background: rgba(var(--bs-danger-rgb), 0.14); color: var(--bs-danger); }
.tw-row__body { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1; }
.tw-row__line { display: flex; align-items: baseline; gap: 8px; min-width: 0; }
.tw-row__title { font-weight: 600; font-size: 0.85rem; color: var(--dw-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; min-width: 0; }
a.tw-row__title:hover { color: var(--bs-primary); }
.tw-row__when { font-size: 0.72rem; color: var(--dw-muted); white-space: nowrap; }
.tw-row__when.is-late { color: var(--bs-danger); font-weight: 600; }
.tw-row__text { font-size: 0.76rem; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tw-row__text a { color: var(--dw-muted); text-decoration: underline; }
.tw-date {
  width: 40px;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--dw-surface-2);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3px 0;
  line-height: 1.1;
}
.tw-date.is-soon { background: rgba(var(--bs-primary-rgb), 0.14); }
.tw-date.is-late { background: rgba(var(--bs-danger-rgb), 0.14); }
.tw-date__day { font-weight: 700; font-size: 0.95rem; }
.tw-date__month { font-size: 0.65rem; text-transform: uppercase; color: var(--dw-muted); }
.tw-route { display: grid; grid-template-columns: minmax(110px, 46%) 1fr auto; align-items: center; gap: 8px; font-size: 0.8rem; }
.tw-route__label { display: flex; align-items: center; gap: 6px; min-width: 0; }
.tw-route__label span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tw-route__label i { color: var(--dw-muted); flex-shrink: 0; }
.tw-route__track { height: 10px; border-radius: 5px; background: var(--dw-surface-2); overflow: hidden; }
.tw-route__fill { display: block; height: 100%; border-radius: 0 4px 4px 0; background: var(--bs-primary); }
.tw-route__value { font-weight: 600; text-align: right; }
</style>
